// src/hooks/useEntityNavigation.ts
// Reusable navigation helper for entity pages with filter preservation

import { useCallback, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import { EntityType, EntityNavigationMode } from "@/types/filters";
import { useEntityFiltersContext } from "@/contexts/EntityFiltersContext/EntityFiltersContext";
import { buildUrlWithFilters } from "@/utils/filterHelpers";

// ============================================
// Types
// ============================================

export interface UseEntityNavigationOptions {
  /** Entity type for URL path building */
  entityType: EntityType;

  /** Whether to preserve filters in navigation URLs (default: true) */
  preserveFilters?: boolean;

  prefix?: string | undefined | null;
}

export interface UseEntityNavigationReturn {
  // ============================================
  // URL Builders
  // ============================================

  /** Get list page URL with current filters */
  getListUrl: () => string;

  /** Get add page URL with current filters */
  getAddUrl: () => string;

  /** Get edit page URL with current filters */
  getEditUrl: (id: string) => string;

  /** Get view page URL with current filters */
  getViewUrl: (id: string) => string;

  /** Get waypoints page URL with current filters */
  getWaypointsUrl: (id: string) => string;

  // ============================================
  // Navigation Actions
  // ============================================

  /** Navigate to list page */
  goToList: () => void;

  /** Navigate to add page */
  goToAdd: () => void;

  /** Navigate to edit page */
  goToEdit: (id: string) => void;

  /** Navigate to view page */
  goToView: (id: string) => void;

  /** Navigate to waypoints page */
  goToWaypoints: (id: string) => void;

  /** Navigate back (to list with filters) */
  goBack: () => void;

  // ============================================
  // Current Route Info
  // ============================================

  /** Current navigation mode based on URL */
  mode: EntityNavigationMode;

  /** Whether currently on add page */
  isAddMode: boolean;

  /** Whether currently on edit page */
  isEditMode: boolean;

  /** Whether currently on view page */
  isViewMode: boolean;

  /** Whether currently on list page */
  isListMode: boolean;

  /** Whether currently on waypoints page */
  isWaypointsMode: boolean;

  /** Current entity ID from URL params (if on edit/view/waypoints page) */
  currentEntityId: string | null;

  /** Base path for the entity type */
  basePath: string;
}

// ============================================
// Hook
// ============================================

export const useEntityNavigation = (
  options: UseEntityNavigationOptions
): UseEntityNavigationReturn => {
  const { entityType, preserveFilters = true, prefix = "" } = options;

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id?: string }>();
  const { filters } = useEntityFiltersContext();

  const basePath =
    "/" + [prefix, entityType].filter((item: any) => !!item).join("/");

  // ============================================
  // URL Builders
  // ============================================

  const buildUrl = useCallback(
    (path: string): string => {
      if (!preserveFilters) {
        return path;
      }
      return buildUrlWithFilters(path, filters);
    },
    [filters, preserveFilters]
  );

  const getListUrl = useCallback((): string => {
    return buildUrl(basePath);
  }, [basePath, buildUrl]);

  const getAddUrl = useCallback((): string => {
    return buildUrl(`${basePath}/add`);
  }, [basePath, buildUrl]);

  const getEditUrl = useCallback(
    (id: string): string => {
      return buildUrl(`${basePath}/${id}/edit`);
    },
    [basePath, buildUrl]
  );

  const getViewUrl = useCallback(
    (id: string): string => {
      return buildUrl(`${basePath}/${id}`);
    },
    [basePath, buildUrl]
  );

  const getWaypointsUrl = useCallback(
    (id: string): string => {
      return buildUrl(`${basePath}/${id}/waypoints`);
    },
    [basePath, buildUrl]
  );

  // ============================================
  // Navigation Actions
  // ============================================

  const goToList = useCallback((): void => {
    navigate(getListUrl());
  }, [navigate, getListUrl]);

  const goToAdd = useCallback((): void => {
    navigate(getAddUrl());
  }, [navigate, getAddUrl]);

  const goToEdit = useCallback(
    (id: string): void => {
      navigate(getEditUrl(id));
    },
    [navigate, getEditUrl]
  );

  const goToView = useCallback(
    (id: string): void => {
      navigate(getViewUrl(id));
    },
    [navigate, getViewUrl]
  );

  const goToWaypoints = useCallback(
    (id: string): void => {
      navigate(getWaypointsUrl(id));
    },
    [navigate, getWaypointsUrl]
  );

  const goBack = useCallback((): void => {
    // Always go to list with current filters
    navigate(getListUrl());
  }, [navigate, getListUrl]);

  // ============================================
  // Current Route Detection
  // ============================================

  const routeInfo = useMemo(() => {
    const pathname = location.pathname;
    const entityId = params.id || null;

    // Check for add mode: /entityType/add
    const isAddMode = pathname.endsWith("/add");

    // Check for waypoints mode: /entityType/:id/waypoints
    const isWaypointsMode = !!entityId && pathname.endsWith("/waypoints");

    // Check for edit mode: /entityType/:id/edit
    const isEditMode = !!entityId && pathname.endsWith("/edit");

    // Check for view mode: /entityType/:id (but not /edit, /add, or /waypoints)
    const isViewMode = !!entityId && !isEditMode && !isAddMode && !isWaypointsMode;

    // Check for list mode: /entityType (exact match or with query params only)
    const isListMode = !isAddMode && !isEditMode && !isViewMode && !isWaypointsMode;

    // Determine mode
    let mode: EntityNavigationMode = "list";
    if (isAddMode) mode = "add";
    else if (isEditMode) mode = "edit";
    else if (isViewMode) mode = "view";
    else if (isWaypointsMode) mode = "waypoints";

    return {
      mode,
      isAddMode,
      isEditMode,
      isViewMode,
      isListMode,
      isWaypointsMode,
      currentEntityId: entityId,
    };
  }, [location.pathname, params.id]);

  // ============================================
  // Return
  // ============================================

  return {
    // URL builders
    getListUrl,
    getAddUrl,
    getEditUrl,
    getViewUrl,
    getWaypointsUrl,

    // Navigation actions
    goToList,
    goToAdd,
    goToEdit,
    goToView,
    goToWaypoints,
    goBack,

    // Current route info
    ...routeInfo,
    basePath,
  };
};